import React, { useState, useEffect } from 'react';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faStore, faTrashAlt, faExclamationTriangle, faCompass, faPlayCircle, faFlask, faComments, faRotate, faStop } from '@fortawesome/free-solid-svg-icons';
import { fastapi } from '../../services/api';
import BtnTaratic from '../../components/ui/BtnTaratic';
import GhostSwitch from '../../components/ui/GhostSwitch';
import DeleteCommerceModal from '../../components/settings/DeleteCommerceModal';
import { useTranslation } from 'react-i18next';
import { getDemoStatus, refreshDemoStatus, resetDemoData, setDemoStatus, type DemoStatus } from '../../services/demoModeService';

const DemoSettingsCard: React.FC = () => {
  const [status, setStatus] = useState<DemoStatus | null>(getDemoStatus());
  const [busy, setBusy] = useState(false);
  const [remaining, setRemaining] = useState(0);

  const load = async () => {
    try { setStatus(await refreshDemoStatus()); } catch { setStatus(null); }
  };

  useEffect(() => { void load(); }, []);
  useEffect(() => {
    const tick = () => setRemaining(status?.active && status.expires_at
      ? Math.max(0, Math.ceil((Date.parse(status.expires_at) - Date.now()) / 1000)) : 0);
    tick();
    const timer = window.setInterval(tick, 1000);
    return () => window.clearInterval(timer);
  }, [status]);

  if (!status?.allowed) return null;
  const time = `${String(Math.floor(remaining / 60)).padStart(2, '0')}:${String(remaining % 60).padStart(2, '0')}`;
  const start = async () => {
    setBusy(true);
    try {
      const { data } = await fastapi.post('/tenants/me/demo/start');
      setDemoStatus(data); resetDemoData();
      sessionStorage.setItem('taratic:demo:open-chat', '1');
      window.location.assign('/stats');
    } finally { setBusy(false); }
  };
  const stop = async () => {
    setBusy(true);
    try { const { data } = await fastapi.post('/tenants/me/demo/stop'); setDemoStatus(data); window.location.reload(); }
    finally { setBusy(false); }
  };
  const openChat = () => { sessionStorage.setItem('taratic:demo:open-chat', '1'); window.location.assign('/stats'); };

  return (
    <div className={`${status.active ? 'bg-gradient-to-r from-cyan-500/25 to-violet-500/20 ring-2 ring-cyan-300/60' : 'bg-cyan-500/10'} rounded-2xl p-6 shadow-inner-glow border border-cyan-300/25`}>
      <div className="flex flex-col lg:flex-row lg:items-center justify-between gap-5">
        <div className="flex items-start gap-4">
          <div className="w-11 h-11 rounded-xl bg-cyan-400/25 text-cyan-200 flex items-center justify-center"><FontAwesomeIcon icon={faFlask} /></div>
          <div>
            <div className="flex items-center gap-3"><h2 className="text-lg font-bold text-white">Demo clínica</h2>{status.active && <span className="px-2 py-1 rounded-full bg-cyan-300 text-slate-950 text-xs font-black">DEMO MODE · {time}</span>}</div>
            <p className="text-sm text-white/75 mt-1 max-w-2xl">{status.active ? 'Los chats, pacientes, notas y citas visibles son sintéticos y están aislados en este navegador.' : 'Inicia una sesión de 10 minutos con conversaciones, notas y agenda clínica realistas. Tus datos reales no se modifican.'}</p>
          </div>
        </div>
        <div className="flex flex-wrap gap-2">
          {!status.active ? <button data-ui="demo-start-button" disabled={busy} onClick={start} className="high-contrast-control px-4 py-2 rounded-xl bg-slate-600 text-white font-bold disabled:opacity-50 disabled:cursor-not-allowed"><FontAwesomeIcon icon={faPlayCircle} className="mr-2" />Start 10-minute demo</button> : <>
            <button onClick={openChat} className="px-3 py-2 rounded-xl bg-white text-slate-950 font-bold"><FontAwesomeIcon icon={faComments} className="mr-2" />Abrir chats</button>
            <button onClick={() => { resetDemoData(); window.location.reload(); }} className="px-3 py-2 rounded-xl bg-white/10 text-white font-semibold"><FontAwesomeIcon icon={faRotate} className="mr-2" />Reiniciar datos</button>
            <button disabled={busy} onClick={stop} className="px-3 py-2 rounded-xl bg-red-500/20 text-red-200 font-semibold disabled:opacity-50"><FontAwesomeIcon icon={faStop} className="mr-2" />Detener demo</button>
          </>}
        </div>
      </div>
    </div>
  );
};

const OnboardingSettingsCard: React.FC = () => {
  const { t } = useTranslation();
  const [tourEnabled, setTourEnabled] = useState<boolean>(true);

  useEffect(() => {
    fetchUserSettings();
  }, []);

  const fetchUserSettings = async () => {
    try {
      const res = await fastapi.get('/auth/me');
      if (res.data && typeof res.data.onboarding_tour_enabled === 'boolean') {
        setTourEnabled(res.data.onboarding_tour_enabled);
      }
    } catch (e) {
      console.warn('Error fetching user tour settings:', e);
    }
  };

  const handleToggle = async (val: boolean) => {
    setTourEnabled(val);
    try {
      await fastapi.patch('/auth/onboarding-tour', { enabled: val });
    } catch (e) {
      console.error('Error updating tour setting:', e);
    }
  };

  const handleLaunchTour = async () => {
    try {
      await fastapi.patch('/auth/onboarding-tour', { completed: false, enabled: true });
      window.location.href = '/stats';
    } catch (e) {
      console.warn('Could not reset tour', e);
    }
  };

  return (
    <div data-ui="general-settings-region-001" className="bg-black/20 backdrop-filter backdrop-blur-xl rounded-2xl p-6 shadow-inner-glow shadow-inner-glow-black-md border-none space-y-4">
      <div data-ui="general-settings-region-002" className="flex items-center justify-between pb-4 border-b border-white/10">
        <div data-ui="general-settings-region-003" className="flex items-center gap-3">
          <div data-ui="general-settings-region-004" className="w-10 h-10 rounded-xl bg-blue-600/30 text-blue-300 flex items-center justify-center shrink-0 border-none shadow-inner-glow shadow-inner-glow-white-sm">
            <FontAwesomeIcon icon={faCompass} className="text-lg icon-inner-glow-white animate-spin-slow" />
          </div>
          <div data-ui="general-settings-region-005">
            <div data-ui="general-settings-region-006" className="flex items-center gap-2">
              <h2 className="text-lg font-bold text-white">
                {t('settings.general.onboarding.title', 'Tour de Bienvenida / Onboarding')}
              </h2>
              <span className="px-2 py-0.5 rounded-full bg-blue-500/20 text-blue-300 text-[10px] font-bold uppercase tracking-wider border-none shadow-inner-glow">
                {t('settings.general.onboarding.badge', 'Guiado')}
              </span>
            </div>
            <p className="text-xs text-white/80 mt-0.5">
              {t('settings.general.onboarding.description', 'Controla la experiencia de tour guiado interactivo por las áreas de la plataforma.')}
            </p>
          </div>
        </div>

        <GhostSwitch
          checked={tourEnabled}
          onChange={handleToggle}
          ariaLabel={t('settings.general.onboarding.title', 'Tour de Bienvenida / Onboarding')}
        />
      </div>

      <div data-ui="general-settings-region-007" className="flex flex-col sm:flex-row items-start sm:items-center justify-between gap-4 pt-2">
        <div data-ui="general-settings-region-008">
          <h3 className="text-sm font-semibold text-white">
            {t('settings.general.onboarding.launchTitle', 'Iniciar o Reiniciar Tour ahora')}
          </h3>
          <p className="text-xs text-white/80 max-w-md mt-0.5 leading-relaxed">
            {t('settings.general.onboarding.launchDescription', 'Vuelve a lanzar el recorrido guiado para explorar todos los módulos disponibles en cualquier momento.')}
          </p>
        </div>

        <BtnTaratic
          color="blue"
          onClick={handleLaunchTour}
          className="flex items-center gap-2 px-4 py-2 text-sm shadow-lg shadow-blue-600/20 shrink-0"
        >
          <FontAwesomeIcon icon={faPlayCircle} />
          <span>{t('settings.general.onboarding.launchButton', 'Iniciar / Reiniciar Tour')}</span>
        </BtnTaratic>
      </div>
    </div>
  );
};

const GeneralSettings: React.FC = () => {
  const { t } = useTranslation();
  const [commerceDetails, setCommerceDetails] = useState<any>(null);
  const [isDeleteModalOpen, setIsDeleteModalOpen] = useState<boolean>(false);

  const comercioKey = localStorage.getItem('comercio_key') || '';

  useEffect(() => {
    if (comercioKey) {
      fetchCommerceInfo();
    }
  }, [comercioKey]);

  const fetchCommerceInfo = async () => {
    try {
      const res = await fastapi.get(`/auth/comercios/${comercioKey}`);
      setCommerceDetails(res.data);
    } catch (e) {
      console.error('Error fetching commerce info:', e);
    }
  };

  const commerceName = commerceDetails?.nombre || comercioKey || 'Mi Comercio';

  return (
    <div data-ui="settings-general-page" className="space-y-8 animate-fade-in-up">
      {/* Header */}
      <div data-ui="general-settings-region-009">
        <h1 className="text-3xl font-black text-white">
          {t('settings.general.title', 'Configuración del Comercio')}
        </h1>
        <p className="text-white/80 mt-1 text-sm font-medium">
          {t('settings.general.subtitle', 'Gestiona los detalles generales y las opciones de cuenta de tu comercio.')}
        </p>
      </div>

      {/* Basic Commerce Information Card */}
      <DemoSettingsCard />

      {/* Basic Commerce Information Card */}
      <div data-ui="general-settings-region-010" className="bg-black/20 backdrop-filter backdrop-blur-xl rounded-2xl p-6 shadow-inner-glow shadow-inner-glow-black-md border-none space-y-4">
        <div data-ui="general-settings-region-011" className="flex items-center gap-3 pb-4 border-b border-white/10">
          <div data-ui="general-settings-region-012" className="w-10 h-10 rounded-xl bg-blue-600/30 text-blue-300 flex items-center justify-center shrink-0 border-none shadow-inner-glow shadow-inner-glow-white-sm">
            <FontAwesomeIcon icon={faStore} className="text-lg icon-inner-glow-white" />
          </div>
          <div data-ui="general-settings-region-013">
            <h2 className="text-lg font-bold text-white">{commerceName}</h2>
            <p className="text-xs font-mono text-white/70">{comercioKey}</p>
          </div>
        </div>

        <div data-ui="general-settings-region-014" className="grid grid-cols-1 md:grid-cols-2 gap-4 text-sm pt-2">
          <div data-ui="general-settings-region-015" className="p-3 bg-black/40 rounded-xl border-none shadow-inner-glow">
            <p className="text-xs text-white/70 uppercase tracking-wider mb-1 font-semibold">
              {t('settings.general.address', 'Dirección')}
            </p>
            <p className="text-white font-medium">{commerceDetails?.direccion || t('settings.general.notSpecified', 'No especificada')}</p>
          </div>
          <div data-ui="general-settings-region-016" className="p-3 bg-black/40 rounded-xl border-none shadow-inner-glow">
            <p className="text-xs text-white/70 uppercase tracking-wider mb-1 font-semibold">
              {t('settings.general.phone', 'Teléfono')}
            </p>
            <p className="text-white font-medium">{commerceDetails?.telefono || t('settings.general.notSpecified', 'No especificado')}</p>
          </div>
          <div data-ui="general-settings-region-017" className="p-3 bg-black/40 rounded-xl border-none shadow-inner-glow">
            <p className="text-xs text-white/70 uppercase tracking-wider mb-1 font-semibold">
              {t('settings.general.timezone', 'Zona Horaria')}
            </p>
            <p className="text-white font-mono font-medium">{commerceDetails?.timezone_iana || 'Europe/Madrid'}</p>
          </div>
          <div data-ui="general-settings-region-018" className="p-3 bg-black/40 rounded-xl border-none shadow-inner-glow">
            <p className="text-xs text-white/70 uppercase tracking-wider mb-1 font-semibold">
              {t('settings.general.category', 'Categoría')}
            </p>
            <p className="text-white font-medium">{commerceDetails?.categoria || 'General'}</p>
          </div>
        </div>
      </div>

      {/* Onboarding Guided Tour Configuration Card */}
      <OnboardingSettingsCard />

      {/* Danger Zone Card */}
      <div data-ui="general-settings-region-019" className="bg-red-500/5 backdrop-filter backdrop-blur-xl rounded-2xl p-6 border-none shadow-inner-glow shadow-inner-glow-black-md space-y-4">
        <div data-ui="general-settings-region-020" className="flex items-center justify-between pb-4 border-b border-red-500/10">
          <div data-ui="general-settings-region-021" className="flex items-center gap-3">
            <div data-ui="general-settings-region-022" className="w-10 h-10 rounded-xl bg-red-500/20 text-red-400 flex items-center justify-center shrink-0 border-none shadow-inner-glow">
              <FontAwesomeIcon icon={faExclamationTriangle} className="text-lg icon-inner-glow-white" />
            </div>
            <div data-ui="general-settings-region-023">
              <h2 className="text-lg font-bold text-red-400">
                {t('settings.general.dangerZone.title', 'Zona de Peligro')}
              </h2>
              <p className="text-xs text-white/80">
                {t('settings.general.dangerZone.description', 'Acciones irreversibles sobre la cuenta de tu comercio.')}
              </p>
            </div>
          </div>
        </div>

        <div data-ui="general-settings-region-024" className="flex flex-col sm:flex-row items-start sm:items-center justify-between gap-4 pt-2">
          <div data-ui="general-settings-region-025">
            <h3 className="text-sm font-semibold text-white">
              {t('settings.general.dangerZone.deleteTitle', 'Borrar este comercio')}
            </h3>
            <p className="text-xs text-white/80 max-w-md mt-0.5 leading-relaxed">
              {t('settings.general.dangerZone.deleteDescription', 'Al confirmar el borrado, se desactivará el comercio y tu correo quedará inmediatamente libre para registrar un nuevo comercio si lo deseas.')}
            </p>
          </div>

          <BtnTaratic
            color="red"
            onClick={() => setIsDeleteModalOpen(true)}
            className="flex items-center gap-2 px-4 py-2 text-sm shrink-0"
          >
            <FontAwesomeIcon icon={faTrashAlt} />
            <span>{t('settings.general.dangerZone.deleteButton', 'Borrar Comercio')}</span>
          </BtnTaratic>
        </div>
      </div>

      {/* Modal component */}
      <DeleteCommerceModal
        isOpen={isDeleteModalOpen}
        onClose={() => setIsDeleteModalOpen(false)}
        commerceName={commerceName}
      />
    </div>
  );
};

export default GeneralSettings;
